/*
 * Account deletion page. Google Play links here from the store listing, so it
 * must name the app, give the steps, and say what is deleted and what is kept.
 * The retention wording must match section 4 of the Privacy Policy.
 */
import { Link } from "wouter";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/**
 * Public page explaining how to delete an H-Plate account.
 */
export default function DeleteAccount() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1 pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="container max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">Your account</p>
          <h1 className="mt-3 font-display text-[40px] leading-[1.08] md:text-[52px] tracking-tight text-slate-900">
            Delete your H-Plate account
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            You can delete your H-Plate account at any time, whether you ride
            as a passenger or drive an H-bus. H-Plate is operated by Nex
            Paradise Ltd in the Commonwealth of Dominica.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-slate-200 p-7">
              <h2 className="font-sans text-lg font-semibold text-slate-900">In the app</h2>
              <ol className="mt-4 space-y-3">
                {[
                  "Open H-Plate and sign in.",
                  "Tap Profile, then tap your name at the top.",
                  "Tap Delete account and confirm.",
                ].map((step, i) => (
                  <li key={step} className="flex items-start gap-3 text-[15px] text-slate-700">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl border border-slate-200 p-7">
              <h2 className="font-sans text-lg font-semibold text-slate-900">By email</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                No access to the app? Email us from the address on your
                account and ask us to delete it. We confirm within 5 working
                days.
              </p>
              <a
                href="mailto:support@h-plate.com?subject=Delete%20my%20H-Plate%20account"
                className="mt-5 inline-block font-semibold text-emerald-800 hover:text-emerald-900"
              >
                support@h-plate.com
              </a>
            </div>
          </div>

          <div className="mt-14 space-y-10">
            <section>
              <h2 className="font-display text-2xl tracking-tight text-slate-900">What we delete</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Your profile (name, email, phone number and date of birth),
                your profile photo, saved places, trip history, and for
                drivers, your vehicle details, licence and permit photos and
                location history. Your sign-in is removed, so the account
                can't be recovered.
              </p>
            </section>
            <section>
              <h2 className="font-display text-2xl tracking-tight text-slate-900">What we may keep</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                A small number of records we need for safety, fraud prevention
                or legal reasons, such as incident reports, are kept only as
                long as needed for those purposes. Crash reports are kept for
                about 90 days. See our{" "}
                <Link href="/privacy" className="font-semibold text-emerald-800 hover:text-emerald-900">
                  Privacy Policy
                </Link>{" "}
                for details.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
